/**
 * Service layer for submitting contact form data.
 * Handles the fetch call and returns a typed result — no React state.
 */

export interface ContactFormSubmission {
  readonly data: {
    readonly firstname: string;
    readonly lastname: string;
    readonly email: string;
    readonly company: string;
    readonly phone?: string;
    readonly note?: string;
  };
  readonly recaptchaToken?: string;
  readonly pageUrl: string;
  readonly utmParams: Record<string, string>;
}

export interface ContactFormResult {
  readonly success: boolean;
  readonly message?: string;
}

/**
 * Submit a contact form payload to the unified forms API.
 * Returns a result object instead of modifying React state.
 */
export async function submitContactForm(
  submission: ContactFormSubmission,
): Promise<ContactFormResult> {
  const response = await fetch("/api/forms/submit", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      formType: "contact",
      data: submission.data,
      recaptchaToken: submission.recaptchaToken,
      pageUrl: submission.pageUrl,
      utmParams: submission.utmParams,
    }),
  });

  if (response.ok) {
    return { success: true };
  }

  try {
    const body = await response.json();
    return { success: false, message: body.message };
  } catch {
    return {
      success: false,
      message: `Server error: ${response.status} ${response.statusText}`,
    };
  }
}

// ---------------------------------------------------------------------------
// Lead capture form (heroForm variant) — submits as formType: 'other'
// ---------------------------------------------------------------------------

export interface LeadCaptureFormSubmission {
  readonly data: {
    readonly firstname?: string;
    readonly lastname?: string;
    readonly email: string;
    readonly company?: string;
    readonly note: string;
    readonly websiteUrl?: string;
  };
  readonly recaptchaToken?: string;
  readonly pageUrl: string;
  readonly utmParams: Record<string, string>;
  /** Optional Salesforce Form_Fill_Type__c override (e.g. "aio_audit") */
  readonly formFillType?: string;
}

/**
 * Submit a lead capture form payload to the unified forms API.
 * Uses formType 'other' which routes to Salesforce Lead creation.
 */
export async function submitLeadCaptureForm(
  submission: LeadCaptureFormSubmission,
): Promise<ContactFormResult> {
  const response = await fetch("/api/forms/submit", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      formType: "other",
      data: submission.data,
      recaptchaToken: submission.recaptchaToken,
      pageUrl: submission.pageUrl,
      utmParams: submission.utmParams,
      formFillType: submission.formFillType,
    }),
  });

  if (response.ok) {
    return { success: true };
  }

  try {
    const body = await response.json();
    return { success: false, message: body.message };
  } catch {
    return {
      success: false,
      message: `Server error: ${response.status} ${response.statusText}`,
    };
  }
}
